import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import { MdOutlineRepeat } from "react-icons/md";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useTranslation } from "react-i18next";
import PageTitle from "../../components/PageTitle/PageTitle";
import { ETransactionType, ICON_SIZES } from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import { useParams } from "react-router-dom";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import useCache from "../../hooks/useCache";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import useMainFilter from "src/hooks/useMainFilter";
import useCurrentUser from "src/hooks/useCurrentUser";
import { AnimatePresence, motion } from "framer-motion";
import PageSubtitleAccount from "src/components/PageTitle/PageSubtitleAccount";
import TodayTotalsBar from "src/components/TodayTotalsBar/TodayTotalsBar";
import CurrentAccountCard from "src/components/CurrentAccountCard/CurrentAccountCard";
import useCollapsibles from "src/hooks/useCollapsibles";
import useAccountsDropdownMenu from "src/hooks/useAccountsDropdownMenu";
import { useEffect, useMemo, useState } from "react";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import AccountNotFound from "src/components/AccountNotFound/AccountNotFound";
import RecurringTransactionsCollapsible, {
  ERecurringTransactionType,
} from "src/components/RecurringTransaction/RecurringTransactionsCollapsible";
import { formatCurrency, getTotalByTransactionType } from "src/helpers/utils";
import useAllTransactions from "src/hooks/useAllTransactions";
import SelectionTotals from "src/components/SelectionTotals/SelectionTotals";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import useDarkMode from "src/hooks/useDarkMode";
import MarqueeRow from "src/components/MarqueeRow/MarqueeRow";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

const RecurringTransactionsPage: React.FC = () => {
  const [canScroll, setCanScroll] = useState(false);
  const { isEnabled: isDarkMode } = useDarkMode();

  const { account: accountParameter } = useParams();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const { t } = useTranslation();
  const { isLoadingAccounts } = useCurrentUser();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const { mainFilter, setMainFilter } = useMainFilter();
  const { isLoadingAllTransactions, isRefetchingAllTransactions } =
    useAllTransactions();
  const {
    currentAccountData,
    activeTransactions,
    isLoadingCurrentAccount,
    currencySymbol,
  } = useCurrentAccount();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isRecurringTransactionsMobileCollapsibleOpen,
    toggleRecurringTransactionsMobileCollapsible,
  } = useCollapsibles();
  const accountsGlobalMenu = useAccountsDropdownMenu({
    linkTo: "recurring",
  });

  const { isMobile } = useMobile();
  const { clearCache } = useCache();

  useEffect(() => {
    setMainFilter({ account: accountParameter });
  }, [accountParameter]);

  const getAvailableMoney = useMemo(() => {
    const income = getTotalByTransactionType({
      type: ETransactionType.INCOME,
      transactions: activeTransactions,
    });
    const expenses = getTotalByTransactionType({
      type: ETransactionType.EXPENSE,
      transactions: activeTransactions,
    });

    return income - expenses;
  }, [activeTransactions]);

  const isLoadingSomething = useMemo(
    () =>
      [
        !!isLoadingCurrentAccount,
        !!isLoadingAccounts,
        !!isLoadingAllTransactions,
        // !!isRefetchingAllTransactions,
      ]?.includes(true),
    [isLoadingCurrentAccount, isLoadingAccounts, isLoadingAllTransactions]
  );

  const mobileMenuMarquee = useMemo(() => {
    return "Transacciones recurrentes";
  }, []);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          isLoadingSomething ||
          !mainFilter?.account ||
          !currentAccountData ||
          currentAccountData?.settings?.disabled ? null : isMobile ? null : (
            <>
              <CollapsibleBlock
                title={t("Cuenta")}
                uppercase
                open={collapsiblePreferences.accountOpen}
                onChange={(isOpen: boolean) =>
                  updateCollapsible({ accountOpen: isOpen })
                }
                badge={formatCurrency({
                  value: getAvailableMoney,
                  currency: currencySymbol,
                })}
              >
                <CurrentAccountCard />
              </CollapsibleBlock>
              <SelectionTotals transactions={selectedRows} />
            </>
          )
        }
        mobileMenuChildren={<MarqueeRow text={mobileMenuMarquee} />}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {mainFilter?.account ? (
              <>
                {isLoadingSomething ? (
                  <LoadingWithTextPage />
                ) : (
                  <>
                    {currentAccountData &&
                    !currentAccountData?.settings?.disabled ? (
                      <>
                        <PageTitle
                          icon={<MdOutlineRepeat size={ICON_SIZES.MD} />}
                          title={t("Transacciones recurrentes")}
                          subtitle={
                            isMobile && mainFilter?.account ? (
                              <PageSubtitleAccount {...currentAccountData} />
                            ) : null
                          }
                          rightSide={
                            <>
                              <ButtonGroup
                                buttons={[
                                  accountsGlobalMenu,
                                  generalOptionsDropdown,
                                ]}
                                gap={1}
                              />
                            </>
                          }
                        />
                        <TodayTotalsBar />

                        <SlideDownUpAnimation
                          show={
                            !!(isMobile && collapsiblePreferences.accountOpen)
                          }
                        >
                          <CurrentAccountCard />
                        </SlideDownUpAnimation>

                        <div
                          className={clsx([
                            "flex",
                            "flex-col",
                            "gap-4",
                            "w-full",
                          ])}
                          id="focused-section"
                        >
                          <motion.div
                            className={clsx([
                              "w-full",
                              "flex",
                              "flex-col",
                              "gap-6",
                            ])}
                            {...(isMobile
                              ? {
                                  initial: {
                                    opacity: 0.99,
                                  },
                                  animate: { opacity: 1 },
                                  transition: { duration: 0.1 },
                                }
                              : {
                                  initial: { opacity: 0 },
                                  animate: { opacity: 1 },
                                  exit: { opacity: 0 },
                                  transition: { duration: 2, ease: "easeOut" },
                                })}
                            onAnimationComplete={() => {
                              if (isMobile) {
                                if (!!collapsiblePreferences.accountOpen) {
                                  setCanScroll(true);
                                } else {
                                }
                              }
                            }}
                          >
                            <>
                              <RecurringTransactionsCollapsible
                                title="Hoy"
                                type={ERecurringTransactionType.TODAY}
                                showSettingsButton={false}
                              />

                              <RecurringTransactionsCollapsible
                                title="Atrasadas"
                                type={ERecurringTransactionType.OVERDUE}
                                showSettingsButton={false}
                              />

                              <RecurringTransactionsCollapsible
                                title="Próximas"
                                type={ERecurringTransactionType.UPCOMING}
                                showSettingsButton={false}
                              />

                              <RecurringTransactionsCollapsible
                                title="Todas"
                                type={ERecurringTransactionType.ALL}
                                onSelectionChange={setSelectedRows}
                              />
                            </>

                            {isMobile && (
                              <SelectionTotals transactions={selectedRows} />
                            )}
                          </motion.div>
                        </div>
                        <ScrollToSection
                          targetId="focused-section"
                          enabled={canScroll}
                        />
                      </>
                    ) : (
                      <AccountNotFound
                        linkTo="recurring"
                        exists={!!currentAccountData}
                        disabled={!!currentAccountData?.settings?.disabled}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountNotFound linkTo="recurring" />
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>
    </>
  );
};

export default RecurringTransactionsPage;
